import * as React from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, Container, Stack, TextField, Typography } from '@mui/material';
import { useAccount } from './AccountContext';
import { accountUpdateError, changePassword, requestEmailChange, updateName } from '../../gateway/accounts';

interface Status {
  readonly severity: 'success' | 'error',
  readonly message: string,
}

function StatusAlert({ status }: { status?: Status }) {
  if (status === undefined) {
    return null;
  }

  return <Alert severity={status.severity}>{status.message}</Alert>;
}

function NameCard() {
  const { account, invalidatePromise } = useAccount();
  const [name, setName] = useState(account?.name ?? '');
  const [status, setStatus] = useState<Status>();

  useEffect(() => setName(account?.name ?? ''), [account?.name]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (name.trim().length === 0) {
      setStatus({ severity: 'error', message: 'Name must not be blank.' });
      return;
    }

    updateName(name.trim())
      .then(invalidatePromise)
      .then(() => setStatus({ severity: 'success', message: 'Your name has been updated.' }))
      .catch(() => setStatus({ severity: 'error', message: 'Unable to update your name.' }));
  };

  return (
    <Card variant="outlined">
      <CardHeader title="Name" sx={{ pb: 0 }} />
      <CardContent>
        <Stack component="form" onSubmit={handleSubmit} noValidate spacing={2}>
          <StatusAlert status={status} />
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            autoComplete="name"
            fullWidth
          />
          <Button type="submit" variant="contained" disableElevation sx={{ alignSelf: 'flex-start' }}
                  disabled={name.trim() === account?.name}>
            Save name
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function EmailCard() {
  const { account } = useAccount();
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [status, setStatus] = useState<Status>();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = newEmail.trim();

    requestEmailChange(currentPassword, email)
      .then(() => {
        setStatus({
          severity: 'success',
          message: `We've sent a confirmation link to ${email}. Your email address will change once you click it.`
        });
        setNewEmail('');
        setCurrentPassword('');
      })
      .catch(error => {
        const details = accountUpdateError(error)?.details;
        setStatus({ severity: 'error', message: details ? `${details}.` : 'Unable to change your email address.' });
      });
  };

  return (
    <Card variant="outlined">
      <CardHeader title="Email address" sx={{ pb: 0 }} />
      <CardContent>
        <Stack component="form" onSubmit={handleSubmit} noValidate spacing={2}>
          <Typography variant="body2">
            You currently log in with <strong>{account?.email}</strong>. We'll send a link to your new address to
            confirm it before anything changes.
          </Typography>
          <StatusAlert status={status} />
          <TextField
            label="New email address"
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            autoComplete="email"
            fullWidth
          />
          <TextField
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            fullWidth
          />
          <Button type="submit" variant="contained" disableElevation sx={{ alignSelf: 'flex-start' }}
                  disabled={newEmail.trim().length === 0 || currentPassword.length === 0}>
            Change email address
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (newPassword.length < 8) {
      setStatus({ severity: 'error', message: 'New password must be at least 8 characters.' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus({ severity: 'error', message: 'New passwords do not match.' });
      return;
    }

    changePassword(currentPassword, newPassword)
      .then(() => {
        setStatus({ severity: 'success', message: 'Your password has been changed.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      })
      .catch(error => {
        const details = accountUpdateError(error)?.details;
        setStatus({ severity: 'error', message: details ? `${details}.` : 'Unable to change your password.' });
      });
  };

  return (
    <Card variant="outlined">
      <CardHeader title="Password" sx={{ pb: 0 }} />
      <CardContent>
        <Stack component="form" onSubmit={handleSubmit} noValidate spacing={2}>
          <StatusAlert status={status} />
          <TextField
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            fullWidth
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
            helperText="At least 8 characters"
            fullWidth
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
          />
          <Button type="submit" variant="contained" disableElevation sx={{ alignSelf: 'flex-start' }}
                  disabled={currentPassword.length === 0 || newPassword.length === 0}>
            Change password
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function AccountSettingsPage() {
  const { account } = useAccount();

  if (account === undefined) {
    return null;
  }

  return (
    <Container maxWidth="sm">
      <Stack spacing={2} sx={{ my: 3 }}>
        <Typography component="h1" variant="h5">Account settings</Typography>
        <NameCard />
        <EmailCard />
        <PasswordCard />
      </Stack>
    </Container>
  );
}
